import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Archive,
  CheckCircle2,
  ImageIcon,
  Loader2,
  Plus,
  RefreshCcw,
  Save,
  Search,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import {
  cleanAssetLibraryCheckerboard,
  createAssetLibraryItem,
  deleteAssetLibraryItem,
  generateAssetLibraryItem,
  generateAssetLibraryVariant,
  getAssetLibraryItems,
  removeAssetLibrarySolidBackground,
  updateAssetLibraryItem,
  uploadAssetLibraryItem,
} from "../api/assetLibrary";

const STATUS_OPTIONS = ["", "needs_review", "available", "generating", "failed", "archived"];
const TYPE_OPTIONS = ["", "image", "svg", "video", "audio", "gif"];
const SOURCE_OPTIONS = ["", "uploaded", "ai_generated", "generated", "curated", "external"];
const GANESH_STAGE_BACKGROUND_PROMPT =
  "Create ONE vertical Ganesh Chaturthi static stage background for a 1080x1920 mobile video canvas. This asset must be background only: saffron, vermillion, antique gold, and warm ivory textures, soft devotional ambient lighting, subtle vignette depth, and empty safe space for overlaid editable text and separate animated decorative layers. Do not include any foreground objects, deity artwork, flowers, garlands, diya lamps, rangoli, particles, or text.";
const GANESH_STAGE_BACKGROUND_NEGATIVE =
  "Ganesha, Ganpati, deity, god, idol, statue, face, silhouette, icon, trunk, hands, mouse, character, person, diya, lamp, flame, marigold, garland, flower, rangoli, particles, sparkles, bell, kalash, foreground object, Shubh Nimantran, invitation text, festival title, typography, letters, words, watermark, logo";
const GANESHA_CENTER_ARTWORK_NEGATIVE =
  "background, scenery, poster, full scene, frame, border, garland, marigold, flower, flower offering, diya, lamp, flame, rangoli, title, text, typography, words, letters, watermark, logo, distorted face, extra limbs, low quality";
const FLOWER_OFFERING_NEGATIVE =
  "Ganesha, Ganpati, deity, idol, statue, silhouette, face, trunk, hands, mouse, garland, marigold garland, frame, border, diya, lamp, flame, rangoli, floor, background, white background, black background, gray background, solid background, checkerboard, transparency grid, full scene, poster, invitation, title, text, typography, letters, words, watermark, logo";
const MARIGOLD_TOP_GARLAND_NEGATIVE =
  "Ganesha, Ganpati, deity, idol, statue, silhouette, face, trunk, hands, mouse, flower offering, loose petals, diya, lamp, flame, rangoli, floor, background, white background, black background, gray background, solid background, checkerboard, transparency grid, full scene, poster, invitation, title, text, typography, letters, words, watermark, logo";
const DIYA_GLOW_OVERLAY_NEGATIVE =
  "Ganesha, Ganpati, deity, idol, statue, visible diya lamp, visible flame, wick, garland, marigold, flower, rangoli, floor, background, white background, black background, gray background, solid background, checkerboard, transparency grid, full scene, poster, invitation, title, text, typography, letters, words, watermark, logo";
const HALO_GLOW_NEGATIVE =
  "Ganesha, Ganpati, deity, idol, statue, face, trunk, hands, garland, marigold, flower, diya, lamp, flame, rangoli, floor, background, white background, black background, gray background, solid background, checkerboard, transparency grid, full scene, poster, invitation, title, text, typography, letters, words, watermark, logo";

function toText(tags) {
  return Array.isArray(tags) ? tags.join(", ") : "";
}

function toTags(value = "") {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function getPreviewUrl(asset) {
  return asset?.thumbnail_url || asset?.file_url || "";
}

function getMediaKind(asset) {
  const type = (asset?.asset_type || "").toLowerCase();
  const url = getPreviewUrl(asset).toLowerCase();

  if (type.includes("video") || /\.(mp4|webm|mov)(\?|#|$)/.test(url)) return "video";
  if (type.includes("audio") || /\.(mp3|wav|ogg|m4a)(\?|#|$)/.test(url)) return "audio";
  if (type.includes("image") || type.includes("svg") || /\.(svg|png|jpe?g|webp|gif)(\?|#|$)/.test(url)) {
    return "image";
  }

  return url ? "link" : "empty";
}

function isBackgroundAsset(asset) {
  const roleText = [asset?.asset_group, asset?.asset_role, asset?.name].filter(Boolean).join(" ").toLowerCase();
  return roleText.includes("background") || roleText.includes("full_scene") || roleText.includes("full scene");
}

function shouldPreferTransparentAsset(asset) {
  const type = (asset?.asset_type || "").toLowerCase();
  return ["image", "svg", "gif"].includes(type) && !isBackgroundAsset(asset);
}

function isGaneshStageBackground(asset) {
  const roleText = [asset?.asset_group, asset?.asset_role, asset?.name]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return roleText.includes("ganesha_hero_background") || (roleText.includes("ganesh") && roleText.includes("background"));
}

function canonicalizeAssetRole(value = "") {
  const normalized = String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

  if (normalized.includes("flower offering left")) return "flower_offering_left";
  if (normalized.includes("flower offering right")) return "flower_offering_right";
  if (normalized.includes("marigold top garland")) return "marigold_top_garland";
  if (normalized.includes("static stage background")) return "static_stage_background";
  if (normalized.includes("ganesha hero background")) return "static_stage_background";
  if (normalized.includes("ganesha center artwork")) return "ganesha_center_artwork";
  if (normalized.includes("ganesha icon") || normalized.includes("ganesha silhouette")) return "ganesha_icon_or_silhouette";
  if (normalized.includes("diya glow overlay")) return "diya_glow_overlay";
  if (normalized.includes("ganesha halo glow") || normalized.includes("hero halo glow")) return "ganesha_halo_glow";

  return "";
}

function getBriefCanonicalRole(brief) {
  return [
    brief?.asset_role,
    brief?.role,
    brief?.asset_key,
    brief?.name,
    brief?.generation_prompt,
    ...(brief?.usage_tags || []),
  ]
    .map(canonicalizeAssetRole)
    .find(Boolean);
}

function buildFlowerOfferingPrompt(role) {
  const direction = role === "flower_offering_right" ? "lower-right toward center" : "lower-left toward center";
  return (
    "Create ONE isolated transparent-background flower offering cutout asset. " +
    "This asset must contain only a compact cluster of marigold blossoms, rose petals, and a few lotus-like petals, " +
    `angled from ${direction} for side-entry animation. Tight crop around the flowers. ` +
    "Transparent background required with real alpha channel. Do not add white, black, gray, solid, scene, or checkerboard background. No other objects."
  );
}

function buildMarigoldTopGarlandPrompt() {
  return (
    "Create ONE isolated transparent-background horizontal marigold top garland cutout asset. " +
    "This asset must contain only an elegant horizontal garland made of orange and yellow marigold flowers, " +
    "small green leaves, and subtle gold thread accents. It should be suitable for sliding down from the top edge of a vertical mobile video. " +
    "Transparent background required with real alpha channel. Do not add white, black, gray, solid, scene, or checkerboard background. No other objects."
  );
}

function buildGaneshaCenterArtworkPrompt() {
  return (
    "Create ONE isolated transparent-background Lord Ganesha center artwork cutout asset for a Ganesh Chaturthi Polotno video template. " +
    "This asset must contain only respectful premium Ganesha artwork, centered and cleanly cropped for reveal animation. " +
    "Transparent background required with real alpha channel. Do not add scenery, frame, garland, flowers, diya, rangoli, text, poster layout, or background."
  );
}

function buildDiyaGlowOverlayPrompt() {
  return (
    "Create ONE isolated transparent-background warm diya glow overlay asset. " +
    "This asset should contain only soft golden-orange light blooms suitable for lower-corner devotional glow in a Polotno video template. " +
    "Transparent background required with real alpha channel. Do not include visible lamps, flames, Ganesha, garlands, flowers, rangoli, scene background, or text."
  );
}

function buildHaloGlowPrompt() {
  return (
    "Create ONE isolated transparent-background soft golden halo glow overlay asset for placing behind Ganesha artwork. " +
    "This asset must contain only a warm circular/radial glow with subtle devotional light falloff. " +
    "Transparent background required with real alpha channel. Do not include Ganesha, flowers, garlands, diya, rangoli, background scenery, or text."
  );
}

function sanitizePendingBrief(brief) {
  const canonicalRole = getBriefCanonicalRole(brief);
  if (
    canonicalRole !== "flower_offering_left" &&
    canonicalRole !== "flower_offering_right" &&
    canonicalRole !== "marigold_top_garland" &&
    canonicalRole !== "static_stage_background" &&
    canonicalRole !== "ganesha_center_artwork" &&
    canonicalRole !== "ganesha_icon_or_silhouette" &&
    canonicalRole !== "diya_glow_overlay" &&
    canonicalRole !== "ganesha_halo_glow"
  ) {
    return brief;
  }

  if (canonicalRole === "static_stage_background") {
    return {
      ...brief,
      asset_role: canonicalRole,
      asset_group: brief.asset_group || "background",
      generation_prompt: GANESH_STAGE_BACKGROUND_PROMPT,
      negative_prompt: [brief.negative_prompt, GANESH_STAGE_BACKGROUND_NEGATIVE].filter(Boolean).join(", "),
      usage_tags: Array.from(new Set([canonicalRole, ...(brief.usage_tags || [])].filter(Boolean))),
    };
  }

  if (canonicalRole === "ganesha_center_artwork" || canonicalRole === "ganesha_icon_or_silhouette") {
    return {
      ...brief,
      asset_role: canonicalRole,
      asset_group: brief.asset_group || "deity",
      generation_prompt: buildGaneshaCenterArtworkPrompt(),
      negative_prompt: [brief.negative_prompt, GANESHA_CENTER_ARTWORK_NEGATIVE].filter(Boolean).join(", "),
      usage_tags: Array.from(new Set([canonicalRole, ...(brief.usage_tags || [])].filter(Boolean))),
    };
  }

  if (canonicalRole === "marigold_top_garland") {
    return {
      ...brief,
      asset_role: canonicalRole,
      asset_group: brief.asset_group || "floral",
      generation_prompt: buildMarigoldTopGarlandPrompt(),
      negative_prompt: [brief.negative_prompt, MARIGOLD_TOP_GARLAND_NEGATIVE].filter(Boolean).join(", "),
      usage_tags: Array.from(new Set([canonicalRole, ...(brief.usage_tags || [])].filter(Boolean))),
    };
  }

  if (canonicalRole === "diya_glow_overlay") {
    return {
      ...brief,
      asset_role: canonicalRole,
      asset_group: brief.asset_group || "overlay",
      generation_prompt: buildDiyaGlowOverlayPrompt(),
      negative_prompt: [brief.negative_prompt, DIYA_GLOW_OVERLAY_NEGATIVE].filter(Boolean).join(", "),
      usage_tags: Array.from(new Set([canonicalRole, ...(brief.usage_tags || [])].filter(Boolean))),
    };
  }

  if (canonicalRole === "ganesha_halo_glow") {
    return {
      ...brief,
      asset_role: canonicalRole,
      asset_group: brief.asset_group || "overlay",
      generation_prompt: buildHaloGlowPrompt(),
      negative_prompt: [brief.negative_prompt, HALO_GLOW_NEGATIVE].filter(Boolean).join(", "),
      usage_tags: Array.from(new Set([canonicalRole, ...(brief.usage_tags || [])].filter(Boolean))),
    };
  }

  return {
    ...brief,
    asset_role: canonicalRole,
    asset_group: brief.asset_group || "floral",
    generation_prompt: buildFlowerOfferingPrompt(canonicalRole),
    negative_prompt: [brief.negative_prompt, FLOWER_OFFERING_NEGATIVE].filter(Boolean).join(", "),
    usage_tags: Array.from(new Set([canonicalRole, ...(brief.usage_tags || [])].filter(Boolean))),
  };
}

function sanitizePendingBriefsContext(context) {
  if (!context || !Array.isArray(context.pendingBriefs)) {
    return context;
  }

  return {
    ...context,
    pendingBriefs: context.pendingBriefs.map(sanitizePendingBrief),
  };
}

function isUngeneratedAssetBrief(asset) {
  const previewUrl = String(getPreviewUrl(asset) || "");
  return (
    asset?.asset_metadata?.has_media === false ||
    previewUrl.startsWith("pending://") ||
    previewUrl.includes("/pending/") ||
    !previewUrl
  );
}

function sanitizeAssetForPromptEditing(asset) {
  if (!asset || !isUngeneratedAssetBrief(asset)) {
    return asset;
  }

  return sanitizePendingBrief(asset);
}

function createFormState(asset) {
  const promptSafeAsset = sanitizeAssetForPromptEditing(asset) || asset;

  return {
    name: promptSafeAsset?.name || "",
    asset_type: promptSafeAsset?.asset_type || "image",
    asset_group: promptSafeAsset?.asset_group || "",
    asset_role: promptSafeAsset?.asset_role || "",
    file_url: promptSafeAsset?.file_url || "",
    thumbnail_url: promptSafeAsset?.thumbnail_url || "",
    storage_path: promptSafeAsset?.storage_path || "",
    source_type: promptSafeAsset?.source_type || "uploaded",
    status: promptSafeAsset?.status || "needs_review",
    event_types_text: toText(promptSafeAsset?.event_types),
    style_tags_text: toText(promptSafeAsset?.style_tags),
    culture_tags_text: toText(promptSafeAsset?.culture_tags),
    religion_tags_text: toText(promptSafeAsset?.religion_tags),
    language_tags_text: toText(promptSafeAsset?.language_tags),
    usage_tags_text: toText(promptSafeAsset?.usage_tags),
    color_tags_text: toText(promptSafeAsset?.color_tags),
    generation_prompt: promptSafeAsset?.generation_prompt || "",
    negative_prompt: promptSafeAsset?.negative_prompt || "",
    license_notes: promptSafeAsset?.license_notes || "",
  };
}

function buildUpdatePayload(form) {
  return {
    name: form.name.trim(),
    asset_type: form.asset_type,
    asset_group: form.asset_group.trim() || null,
    asset_role: form.asset_role.trim() || null,
    file_url: form.file_url.trim(),
    thumbnail_url: form.thumbnail_url.trim() || null,
    storage_path: form.storage_path.trim() || null,
    source_type: form.source_type,
    status: form.status,
    event_types: toTags(form.event_types_text),
    style_tags: toTags(form.style_tags_text),
    culture_tags: toTags(form.culture_tags_text),
    religion_tags: toTags(form.religion_tags_text),
    language_tags: toTags(form.language_tags_text),
    usage_tags: toTags(form.usage_tags_text),
    color_tags: toTags(form.color_tags_text),
    generation_prompt: form.generation_prompt.trim() || null,
    negative_prompt: form.negative_prompt.trim() || null,
    license_notes: form.license_notes.trim() || null,
  };
}

function searchText(asset) {
  return [
    asset?.name,
    asset?.asset_type,
    asset?.asset_group,
    asset?.asset_role,
    asset?.source_type,
    asset?.status,
    ...(asset?.event_types || []),
    ...(asset?.style_tags || []),
    ...(asset?.culture_tags || []),
    ...(asset?.usage_tags || []),
    ...(asset?.color_tags || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function AdminAssetLibraryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [assets, setAssets] = useState([]);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [form, setForm] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [creatingPendingIndex, setCreatingPendingIndex] = useState(null);
  const [pendingBriefsContext, setPendingBriefsContext] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusTone, setStatusTone] = useState("idle");
  const [uploadForm, setUploadForm] = useState({
    file: null,
    name: "",
    asset_type: "image",
    asset_group: "",
    asset_role: "",
    style_tags_text: "",
    usage_tags_text: "",
    color_tags_text: "",
    license_notes: "",
  });
  const [filters, setFilters] = useState({
    query: "",
    status: "",
    asset_type: "",
    asset_group: "",
    source_type: "",
  });

  const selectedAsset = useMemo(
    () => assets.find((asset) => String(asset.id) === String(selectedAssetId)) || null,
    [assets, selectedAssetId]
  );

  const groupOptions = useMemo(() => {
    const groups = assets.map((asset) => asset.asset_group).filter(Boolean);
    return ["", ...Array.from(new Set(groups)).sort()];
  }, [assets]);

  const filteredAssets = useMemo(() => {
    const query = filters.query.trim().toLowerCase();
    return assets.filter((asset) => {
      const matchesQuery = !query || searchText(asset).includes(query);
      const matchesStatus = !filters.status || asset.status === filters.status;
      const matchesType = !filters.asset_type || asset.asset_type === filters.asset_type;
      const matchesGroup = !filters.asset_group || asset.asset_group === filters.asset_group;
      const matchesSource = !filters.source_type || asset.source_type === filters.source_type;
      return matchesQuery && matchesStatus && matchesType && matchesGroup && matchesSource;
    });
  }, [assets, filters]);

  const loadAssets = async ({ preserveSelection = false } = {}) => {
    setIsLoading(true);
    try {
      const data = await getAssetLibraryItems({ limit: 500 });
      setAssets(data || []);
      setSelectedAssetId((currentId) => {
        if (preserveSelection && currentId && data?.some((asset) => String(asset.id) === String(currentId))) {
          return currentId;
        }
        return data?.[0]?.id || null;
      });
      setStatusMessage("");
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to load asset library.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAssets();
  }, []);

  useEffect(() => {
    const stateBriefs = Array.isArray(location.state?.pendingBriefs) ? location.state : null;

    if (stateBriefs) {
      const sanitizedStateBriefs = sanitizePendingBriefsContext(stateBriefs);
      setPendingBriefsContext(sanitizedStateBriefs);
      window.sessionStorage.setItem("admin-asset-library-pending-briefs", JSON.stringify(sanitizedStateBriefs));
      return;
    }

    try {
      const stored = window.sessionStorage.getItem("admin-asset-library-pending-briefs");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed?.pendingBriefs) && parsed.pendingBriefs.length) {
          const sanitizedStoredBriefs = sanitizePendingBriefsContext(parsed);
          setPendingBriefsContext(sanitizedStoredBriefs);
          window.sessionStorage.setItem("admin-asset-library-pending-briefs", JSON.stringify(sanitizedStoredBriefs));
        }
      }
    } catch (error) {
      console.warn("Unable to restore pending asset briefs:", error);
    }
  }, [location.state]);

  useEffect(() => {
    setForm(selectedAsset ? createFormState(selectedAsset) : null);
  }, [selectedAsset]);

  const updateFilter = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setStatusMessage("");
  };

  const updateUploadField = (field, value) => {
    setUploadForm((current) => ({ ...current, [field]: value }));
    setStatusMessage("");
  };

  const createAssetBrief = async () => {
    setIsCreating(true);
    try {
      const created = await createAssetLibraryItem({
        name: "Untitled asset brief",
        asset_type: "image",
        asset_group: "manual",
        asset_role: "new_asset",
        file_url: `/template-assets/generated/pending/manual-${Date.now()}.png`,
        source_type: "ai_generated",
        status: "needs_review",
        event_types: [],
        style_tags: [],
        usage_tags: [],
        color_tags: [],
        generation_prompt: "",
        negative_prompt: "solid background, white background, black background, gradient background, paper texture, scene, checkerboard, transparency grid, gray squares, fake transparent background, watermark, logo, text",
        license_notes: "",
      });
      setAssets((current) => [created, ...current]);
      setSelectedAssetId(created.id);
      setStatusTone("success");
      setStatusMessage("New asset brief created. Add details, save, then generate or upload media.");
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to create asset brief.");
    } finally {
      setIsCreating(false);
    }
  };

  const clearPendingBriefs = () => {
    setPendingBriefsContext(null);
    window.sessionStorage.removeItem("admin-asset-library-pending-briefs");
  };

  const createPendingBrief = async (brief, index) => {
    setCreatingPendingIndex(index);
    setStatusTone("idle");
    setStatusMessage("Creating suggested asset brief...");

    try {
      const created = await createAssetLibraryItem(sanitizePendingBrief(brief));
      setAssets((current) => [created, ...current]);
      setSelectedAssetId(created.id);
      setPendingBriefsContext((current) => {
        if (!current) return current;
        const nextBriefs = current.pendingBriefs.filter((_, briefIndex) => briefIndex !== index);
        if (!nextBriefs.length) {
          window.sessionStorage.removeItem("admin-asset-library-pending-briefs");
          return null;
        }
        const next = { ...current, pendingBriefs: nextBriefs };
        window.sessionStorage.setItem("admin-asset-library-pending-briefs", JSON.stringify(next));
        return next;
      });
      setStatusTone("success");
      setStatusMessage("Suggested asset brief created. Generate or upload media when ready.");
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to create suggested asset brief.");
    } finally {
      setCreatingPendingIndex(null);
    }
  };

  const createAllPendingBriefs = async () => {
    const briefs = pendingBriefsContext?.pendingBriefs || [];
    if (!briefs.length) return;

    setIsCreating(true);
    setStatusTone("idle");
    setStatusMessage(`Creating ${briefs.length} suggested asset briefs...`);

    try {
      const createdAssets = [];
      for (const brief of briefs) {
        const created = await createAssetLibraryItem(sanitizePendingBrief(brief));
        createdAssets.push(created);
      }
      setAssets((current) => [...createdAssets.reverse(), ...current]);
      setSelectedAssetId(createdAssets[createdAssets.length - 1]?.id || null);
      clearPendingBriefs();
      setStatusTone("success");
      setStatusMessage(`${createdAssets.length} suggested asset briefs created. Generate/review them from the library.`);
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to create all suggested asset briefs.");
    } finally {
      setIsCreating(false);
    }
  };

  const uploadAsset = async () => {
    if (!uploadForm.file) {
      setStatusTone("error");
      setStatusMessage("Choose a file to upload.");
      return;
    }

    setIsUploading(true);
    try {
      const uploaded = await uploadAssetLibraryItem({
        file: uploadForm.file,
        name: uploadForm.name,
        asset_type: uploadForm.asset_type,
        asset_group: uploadForm.asset_group,
        asset_role: uploadForm.asset_role,
        source_type: "uploaded",
        status: "needs_review",
        style_tags: toTags(uploadForm.style_tags_text),
        usage_tags: toTags(uploadForm.usage_tags_text),
        color_tags: toTags(uploadForm.color_tags_text),
        license_notes: uploadForm.license_notes,
      });
      setAssets((current) => [uploaded, ...current]);
      setSelectedAssetId(uploaded.id);
      setShowUploadForm(false);
      setUploadForm({
        file: null,
        name: "",
        asset_type: "image",
        asset_group: "",
        asset_role: "",
        style_tags_text: "",
        usage_tags_text: "",
        color_tags_text: "",
        license_notes: "",
      });
      setStatusTone("success");
      setStatusMessage("Asset uploaded. Review it before marking available.");
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to upload asset.");
    } finally {
      setIsUploading(false);
    }
  };

  const saveAsset = async () => {
    if (!selectedAsset || !form) return;
    if (!form.name.trim() || !form.file_url.trim()) {
      setStatusTone("error");
      setStatusMessage("Name and file URL are required.");
      return;
    }

    setIsSaving(true);
    try {
      const updated = await updateAssetLibraryItem(selectedAsset.id, buildUpdatePayload(form));
      setAssets((current) => current.map((asset) => (asset.id === updated.id ? updated : asset)));
      setStatusTone("success");
      setStatusMessage("Asset metadata saved.");
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to save asset.");
    } finally {
      setIsSaving(false);
    }
  };

  const syncPromptBeforeGeneration = async () => {
    if (!selectedAsset || !form) return selectedAsset;

    const payload = buildUpdatePayload(form);
    const promptChanged =
      payload.generation_prompt !== (selectedAsset.generation_prompt || null) ||
      payload.negative_prompt !== (selectedAsset.negative_prompt || null) ||
      payload.asset_role !== (selectedAsset.asset_role || null) ||
      payload.asset_group !== (selectedAsset.asset_group || null);

    if (!promptChanged) {
      return selectedAsset;
    }

    const updated = await updateAssetLibraryItem(selectedAsset.id, payload);
    setAssets((current) => current.map((asset) => (asset.id === updated.id ? updated : asset)));
    return updated;
  };

  const generateAsset = async () => {
    if (!selectedAsset || !form) return;
    if (!form.generation_prompt) {
      setStatusTone("error");
      setStatusMessage("Add and save a generation prompt before generating media.");
      return;
    }

    setIsGenerating(true);
    setStatusTone("idle");
    setStatusMessage("Generating asset media...");

    try {
      const promptSafeAsset = await syncPromptBeforeGeneration();
      const generated = await generateAssetLibraryItem(promptSafeAsset.id);
      setAssets((current) => current.map((asset) => (asset.id === generated.id ? generated : asset)));
      setStatusTone("success");
      setStatusMessage("Asset generated. Review it before marking available.");
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to generate asset.");
      await loadAssets({ preserveSelection: true });
    } finally {
      setIsGenerating(false);
    }
  };

  const generateVariant = async () => {
    if (!selectedAsset || !form) return;
    if (!form.generation_prompt) {
      setStatusTone("error");
      setStatusMessage("Add and save a generation prompt before generating a variant.");
      return;
    }

    setIsGenerating(true);
    setStatusTone("idle");
    setStatusMessage("Generating a new asset variant...");

    try {
      const promptSafeAsset = await syncPromptBeforeGeneration();
      const generated = await generateAssetLibraryVariant(promptSafeAsset.id);
      setAssets((current) => [generated, ...current]);
      setSelectedAssetId(generated.id);
      setStatusTone("success");
      setStatusMessage("Variant generated. Review it before marking available.");
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to generate asset variant.");
      await loadAssets({ preserveSelection: true });
    } finally {
      setIsGenerating(false);
    }
  };

  const cleanCheckerboard = async (mode = "safe") => {
    if (!selectedAsset) return;

    setIsCleaning(true);
    setStatusTone("idle");
    setStatusMessage(
      mode === "aggressive"
        ? "Aggressively cleaning checkerboard background..."
        : "Cleaning checkerboard background..."
    );

    try {
      const cleaned = await cleanAssetLibraryCheckerboard(selectedAsset.id, mode);
      setAssets((current) => current.map((asset) => (asset.id === cleaned.id ? cleaned : asset)));
      setStatusTone("success");
      const cleanupStatus = cleaned.asset_metadata?.manual_checkerboard_cleanup;
      setStatusMessage(
        cleanupStatus === "applied"
          ? "Checkerboard cleanup applied. Review the asset before approving."
          : `Cleanup checked but was not applied: ${cleanupStatus || "no removable background found"}.`
      );
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to clean checkerboard background.");
    } finally {
      setIsCleaning(false);
    }
  };

  const removeSolidBackground = async (mode = "safe") => {
    if (!selectedAsset) return;

    setIsCleaning(true);
    setStatusTone("idle");
    setStatusMessage(
      mode === "aggressive"
        ? "Aggressively removing solid edge background..."
        : "Removing solid edge background..."
    );

    try {
      const cleaned = await removeAssetLibrarySolidBackground(selectedAsset.id, mode);
      setAssets((current) => current.map((asset) => (asset.id === cleaned.id ? cleaned : asset)));
      setStatusTone("success");
      const cleanupStatus = cleaned.asset_metadata?.manual_solid_background_cleanup;
      setStatusMessage(
        cleanupStatus === "applied"
          ? "Solid background removed. Review the transparent asset before approving."
          : `Solid background cleanup checked but was not applied: ${
              cleanupStatus || "no removable edge background found"
            }.`
      );
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to remove solid background.");
    } finally {
      setIsCleaning(false);
    }
  };

  const useGaneshStagePrompt = () => {
    setForm((current) => ({
      ...current,
      generation_prompt: GANESH_STAGE_BACKGROUND_PROMPT,
      negative_prompt: GANESH_STAGE_BACKGROUND_NEGATIVE,
      asset_group: current.asset_group || "background",
      asset_role: "ganesha_hero_background",
      asset_type: current.asset_type || "image",
      usage_tags_text: [
        ...new Set([
          ...toTags(current.usage_tags_text || ""),
          "ganesh_chaturthi",
          "background",
          "hero",
          "devotional",
          "no_deity",
        ]),
      ].join(", "),
    }));
    setStatusTone("idle");
    setStatusMessage("Stage background prompt applied. Save metadata, then generate media or generate as new variant.");
  };

  const setReviewStatus = async (status) => {
    if (!selectedAsset || !form) return;

    setIsSaving(true);
    try {
      const updated = await updateAssetLibraryItem(selectedAsset.id, {
        ...buildUpdatePayload(form),
        status,
      });
      setAssets((current) => current.map((asset) => (asset.id === updated.id ? updated : asset)));
      setStatusTone("success");
      setStatusMessage(
        status === "available"
          ? "Asset approved and available for template use."
          : `Asset marked ${status.replace(/_/g, " ")}.`
      );
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to update asset status.");
    } finally {
      setIsSaving(false);
    }
  };

  const deleteAsset = async () => {
    if (!selectedAsset) return;
    const confirmed = window.confirm(`Delete asset "${selectedAsset.name}"?`);
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await deleteAssetLibraryItem(selectedAsset.id);
      setAssets((current) => current.filter((asset) => asset.id !== selectedAsset.id));
      setSelectedAssetId(null);
      setStatusTone("success");
      setStatusMessage("Asset deleted.");
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to delete asset.");
    } finally {
      setIsDeleting(false);
    }
  };

  const previewUrl = getPreviewUrl(selectedAsset);
  const mediaKind = getMediaKind(selectedAsset);
  const prefersTransparency = shouldPreferTransparentAsset(selectedAsset);
  const intendedUse = selectedAsset?.asset_metadata?.intended_use || "";
  const intendedSize =
    selectedAsset?.asset_metadata?.intended_width && selectedAsset?.asset_metadata?.intended_height
      ? `${selectedAsset.asset_metadata.intended_width}x${selectedAsset.asset_metadata.intended_height}`
      : "";
  const generatedSize = selectedAsset?.asset_metadata?.generated_size || "";
  const finalImageDimensions = selectedAsset?.asset_metadata?.final_image_dimensions || "";
  const canUseGaneshStagePrompt = isGaneshStageBackground(selectedAsset);

  return (
    <div className="min-h-screen bg-[#f8f5ef] text-[#251d18]">
      <section className="border-b border-[#eadfce] bg-[linear-gradient(135deg,#fffaf2_0%,#f3eadc_100%)]">
        <div className="mx-auto w-full max-w-[1480px] px-4 py-8 md:px-8 xl:px-10">
          <button
            type="button"
            onClick={() => navigate("/admin/templates")}
            className="inline-flex items-center gap-2 rounded-full border border-[#e2d3bd] bg-white px-4 py-2 text-sm font-bold text-[#6f4a1f] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
          >
            <ArrowLeft size={16} />
            Back to templates
          </button>
          <div className="mt-6 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl">
              <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#9a7444]">
                Admin asset library
              </p>
              <h1 className="mt-3 font-[Georgia] text-[clamp(2rem,4vw,3.5rem)] font-black leading-tight text-[#251d18]">
                Curate reusable invitation assets
              </h1>
              <p className="mt-4 text-base leading-7 text-[#766650] md:text-lg">
                Review asset briefs, edit metadata, generate media, and approve reusable
                ingredients for templates.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={createAssetBrief}
                disabled={isCreating}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#ff0099] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-black disabled:opacity-60"
              >
                {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus size={16} />}
                Create asset brief
              </button>
              <button
                type="button"
                onClick={() => setShowUploadForm((current) => !current)}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#e2d3bd] bg-white px-5 py-3 text-sm font-bold text-[#6f4a1f] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
              >
                <Upload size={16} />
                Upload asset
              </button>
              <button
                type="button"
                onClick={() => loadAssets({ preserveSelection: true })}
                disabled={isLoading}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#251d18] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#ff0099] disabled:opacity-60"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw size={16} />}
                Refresh
              </button>
            </div>
          </div>
          {statusMessage ? (
            <div
              className={`mt-5 rounded-2xl border px-4 py-3 text-sm font-semibold ${
                statusTone === "error"
                  ? "border-red-200 bg-red-50 text-red-700"
                  : statusTone === "success"
                  ? "border-green-200 bg-green-50 text-green-700"
                  : "border-[#e2d3bd] bg-white text-[#766650]"
              }`}
            >
              {statusMessage}
            </div>
          ) : null}
          {pendingBriefsContext?.pendingBriefs?.length ? (
            <div className="mt-5 rounded-[28px] border border-[#f1d8a4] bg-[#fff9e9] p-4 shadow-sm">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#9a6a10]">
                    Pending AI asset requirements
                  </p>
                  <h2 className="mt-2 text-xl font-black text-[#3f2d1d]">
                    {pendingBriefsContext.pendingBriefs.length} suggested briefs from{" "}
                    {pendingBriefsContext.templateName || "AI template creation"}
                  </h2>
                  <p className="mt-2 max-w-3xl text-sm leading-6 text-[#755c37]">
                    These suggestions are preserved from the template creation page. Create them as
                    library briefs here, then generate or upload the actual media after review.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={createAllPendingBriefs}
                    disabled={isCreating}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#3f2d1d] px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-[#ff0099] disabled:opacity-60"
                  >
                    {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus size={14} />}
                    Create all briefs
                  </button>
                  <button
                    type="button"
                    onClick={clearPendingBriefs}
                    className="rounded-full border border-[#d9a84d] bg-white px-4 py-2.5 text-xs font-bold text-[#7c5412] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {pendingBriefsContext.pendingBriefs.map((brief, index) => (
                  <div key={`${brief.asset_role || brief.name}-${index}`} className="rounded-2xl border border-[#efd9b4] bg-white/75 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-black text-[#3f2d1d]">{brief.name}</p>
                        <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#a77a23]">
                          {[brief.asset_group, brief.asset_role, brief.asset_type].filter(Boolean).join(" | ")}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => createPendingBrief(brief, index)}
                        disabled={creatingPendingIndex === index || isCreating}
                        className="inline-flex shrink-0 items-center justify-center rounded-full bg-[#fff2d6] px-3 py-1.5 text-xs font-bold text-[#7c5412] transition-colors hover:bg-[#ff0099] hover:text-white disabled:opacity-60"
                      >
                        {creatingPendingIndex === index ? "Creating..." : "Create"}
                      </button>
                    </div>
                    <p className="mt-2 line-clamp-3 text-xs leading-5 text-[#755c37]">
                      {brief.generation_prompt || "No generation prompt provided yet."}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </section>

      <main className="mx-auto grid w-full max-w-[1480px] gap-6 px-4 py-8 md:px-8 xl:grid-cols-[minmax(0,1fr)_480px] xl:px-10">
        <section className="rounded-[32px] border border-[#e8dccb] bg-white p-5 shadow-sm md:p-6">
          {showUploadForm ? (
            <div className="mb-6 rounded-[28px] border border-[#e8dccb] bg-[#fbf6ed] p-5">
              <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                <div>
                  <p className="text-sm font-black text-[#251d18]">Upload existing asset</p>
                  <p className="mt-1 text-sm text-[#766650]">
                    Uploaded assets are saved as needs review first, then approved after metadata checks.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowUploadForm(false)}
                  className="text-sm font-bold text-[#9a7444] hover:text-[#ff0099]"
                >
                  Close
                </button>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <input
                  type="file"
                  onChange={(event) => updateUploadField("file", event.target.files?.[0] || null)}
                  className="rounded-2xl border border-[#e8dccb] bg-white px-4 py-3 text-sm md:col-span-2"
                />
                <input
                  value={uploadForm.name}
                  onChange={(event) => updateUploadField("name", event.target.value)}
                  placeholder="Asset name"
                  className="rounded-2xl border border-[#e8dccb] bg-white px-4 py-3 text-sm outline-none focus:border-[#ff0099]"
                />
                <select
                  value={uploadForm.asset_type}
                  onChange={(event) => updateUploadField("asset_type", event.target.value)}
                  className="rounded-2xl border border-[#e8dccb] bg-white px-4 py-3 text-sm outline-none focus:border-[#ff0099]"
                >
                  {TYPE_OPTIONS.filter(Boolean).map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <input
                  value={uploadForm.asset_group}
                  onChange={(event) => updateUploadField("asset_group", event.target.value)}
                  placeholder="Group, e.g. wedding_decor"
                  className="rounded-2xl border border-[#e8dccb] bg-white px-4 py-3 text-sm outline-none focus:border-[#ff0099]"
                />
                <input
                  value={uploadForm.asset_role}
                  onChange={(event) => updateUploadField("asset_role", event.target.value)}
                  placeholder="Role, e.g. mandala"
                  className="rounded-2xl border border-[#e8dccb] bg-white px-4 py-3 text-sm outline-none focus:border-[#ff0099]"
                />
                <input
                  value={uploadForm.style_tags_text}
                  onChange={(event) => updateUploadField("style_tags_text", event.target.value)}
                  placeholder="Style tags"
                  className="rounded-2xl border border-[#e8dccb] bg-white px-4 py-3 text-sm outline-none focus:border-[#ff0099]"
                />
                <input
                  value={uploadForm.color_tags_text}
                  onChange={(event) => updateUploadField("color_tags_text", event.target.value)}
                  placeholder="Color tags"
                  className="rounded-2xl border border-[#e8dccb] bg-white px-4 py-3 text-sm outline-none focus:border-[#ff0099]"
                />
              </div>
              <textarea
                value={uploadForm.license_notes}
                onChange={(event) => updateUploadField("license_notes", event.target.value)}
                placeholder="License notes or source attribution"
                rows={2}
                className="mt-3 w-full rounded-2xl border border-[#e8dccb] bg-white px-4 py-3 text-sm outline-none focus:border-[#ff0099]"
              />
              <button
                type="button"
                onClick={uploadAsset}
                disabled={isUploading}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#251d18] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#ff0099] disabled:opacity-60"
              >
                {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload size={16} />}
                Upload to library
              </button>
            </div>
          ) : null}

          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-[#9a7444]">
                <Search size={16} />
                Library
              </div>
              <p className="mt-2 text-sm text-[#766650]">{filteredAssets.length} assets shown</p>
            </div>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-6">
            <label className="flex items-center gap-2 rounded-2xl border border-[#e8dccb] px-4 py-3 md:col-span-2 xl:col-span-2">
              <Search size={16} className="text-[#9a7444]" />
              <input
                value={filters.query}
                onChange={(event) => updateFilter("query", event.target.value)}
                placeholder="Search name, role, tags, colors..."
                className="min-w-0 flex-1 border-0 bg-transparent text-sm outline-none"
              />
            </label>
            <select
              value={filters.status}
              onChange={(event) => updateFilter("status", event.target.value)}
              className="rounded-2xl border border-[#e8dccb] px-4 py-3 text-sm outline-none focus:border-[#ff0099]"
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status || "all-status"} value={status}>
                  {status ? status.replace(/_/g, " ") : "All statuses"}
                </option>
              ))}
            </select>
            <select
              value={filters.asset_type}
              onChange={(event) => updateFilter("asset_type", event.target.value)}
              className="rounded-2xl border border-[#e8dccb] px-4 py-3 text-sm outline-none focus:border-[#ff0099]"
            >
              {TYPE_OPTIONS.map((type) => (
                <option key={type || "all-types"} value={type}>
                  {type || "All types"}
                </option>
              ))}
            </select>
            <select
              value={filters.asset_group}
              onChange={(event) => updateFilter("asset_group", event.target.value)}
              className="rounded-2xl border border-[#e8dccb] px-4 py-3 text-sm outline-none focus:border-[#ff0099]"
            >
              {groupOptions.map((group) => (
                <option key={group || "all-groups"} value={group}>
                  {group || "All groups"}
                </option>
              ))}
            </select>
            <select
              value={filters.source_type}
              onChange={(event) => updateFilter("source_type", event.target.value)}
              className="rounded-2xl border border-[#e8dccb] px-4 py-3 text-sm outline-none focus:border-[#ff0099]"
            >
              {SOURCE_OPTIONS.map((source) => (
                <option key={source || "all-sources"} value={source}>
                  {source ? source.replace(/_/g, " ") : "All sources"}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            {isLoading ? (
              <div className="rounded-2xl border border-[#e8dccb] bg-[#fbf6ed] p-4 text-sm text-[#766650]">
                Loading assets...
              </div>
            ) : filteredAssets.length ? (
              filteredAssets.map((asset) => {
                const isSelected = String(asset.id) === String(selectedAssetId);
                const assetPreview = getPreviewUrl(asset);

                return (
                  <button
                    key={asset.id}
                    type="button"
                    onClick={() => setSelectedAssetId(asset.id)}
                    className={`rounded-[24px] border p-4 text-left transition-colors ${
                      isSelected ? "border-[#ff0099] bg-[#fff6fb]" : "border-[#e8dccb] bg-white hover:border-[#d7c4a8]"
                    }`}
                  >
                    <div className="flex gap-3">
                      <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl border border-[#e8dccb] bg-[#fbf6ed]">
                        {assetPreview ? (
                          <img src={assetPreview} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center text-[#a78c65]">
                            <ImageIcon size={22} />
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-black text-[#251d18]">{asset.name}</p>
                        <p className="mt-1 text-xs text-[#766650]">
                          {[asset.asset_group, asset.asset_role, asset.asset_type].filter(Boolean).join(" | ")}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="rounded-full bg-[#f3eadc] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#6f4a1f]">
                            {asset.status || "available"}
                          </span>
                          <span className="rounded-full bg-[#f8eef4] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#9b3b71]">
                            {asset.source_type || "uploaded"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="rounded-2xl border border-[#e8dccb] bg-[#fbf6ed] p-4 text-sm text-[#766650]">
                No assets match these filters.
              </div>
            )}
          </div>
        </section>

        <aside className="h-fit rounded-[32px] border border-[#e8dccb] bg-white p-5 shadow-sm xl:sticky xl:top-6">
          {selectedAsset && form ? (
            <>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#9a7444]">
                Asset detail
              </p>
              <div className="mt-4 overflow-hidden rounded-[28px] border border-[#e8dccb] bg-[#fbf6ed]">
                {mediaKind === "image" && previewUrl ? (
                  <img src={previewUrl} alt="" className="h-72 w-full object-contain" />
                ) : mediaKind === "video" && previewUrl ? (
                  <video src={previewUrl} controls className="h-72 w-full bg-black object-contain" />
                ) : mediaKind === "audio" && previewUrl ? (
                  <div className="flex h-72 items-center justify-center p-6">
                    <audio src={previewUrl} controls className="w-full" />
                  </div>
                ) : (
                  <div className="flex h-72 flex-col items-center justify-center gap-2 text-[#a78c65]">
                    <ImageIcon size={32} />
                    <p className="text-sm font-bold">No visual preview</p>
                  </div>
                )}
              </div>
              <div className="mt-4 rounded-2xl border border-[#e8dccb] bg-[#fffaf2] p-4">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-[#9a7444]">
                  Review workflow
                </p>
                <p className="mt-2 text-sm leading-6 text-[#766650]">
                  Check the preview, prompt, tags, and license notes. Approving makes this asset available for templates.
                  {prefersTransparency ? " This asset should be a transparent overlay with no visible background." : ""}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {intendedUse ? (
                    <span className="rounded-full bg-white px-3 py-1 text-[11px] font-bold text-[#6f4a1f]">
                      Intended: {intendedUse.replace(/_/g, " ")}
                    </span>
                  ) : null}
                  {intendedSize ? (
                    <span className="rounded-full bg-white px-3 py-1 text-[11px] font-bold text-[#6f4a1f]">
                      Canvas: {intendedSize}
                    </span>
                  ) : null}
                  {generatedSize ? (
                    <span className="rounded-full bg-white px-3 py-1 text-[11px] font-bold text-[#6f4a1f]">
                      Generated: {generatedSize}
                    </span>
                  ) : null}
                  {finalImageDimensions ? (
                    <span className="rounded-full bg-white px-3 py-1 text-[11px] font-bold text-[#6f4a1f]">
                      Final: {finalImageDimensions}
                    </span>
                  ) : null}
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  <button
                    type="button"
                    onClick={() => setReviewStatus("available")}
                    disabled={isSaving}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-green-600 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-green-700 disabled:opacity-60"
                  >
                    <CheckCircle2 size={14} />
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => setReviewStatus("needs_review")}
                    disabled={isSaving}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[#e2d3bd] bg-white px-3 py-2 text-xs font-bold text-[#6f4a1f] transition-colors hover:border-[#ff0099] hover:text-[#ff0099] disabled:opacity-60"
                  >
                    <RefreshCcw size={14} />
                    Needs review
                  </button>
                  <button
                    type="button"
                    onClick={() => setReviewStatus("archived")}
                    disabled={isSaving}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[#e2d3bd] bg-white px-3 py-2 text-xs font-bold text-[#6f4a1f] transition-colors hover:border-[#ff0099] hover:text-[#ff0099] disabled:opacity-60"
                  >
                    <Archive size={14} />
                    Archive
                  </button>
                </div>
              </div>

              <div className="mt-5 grid gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-bold text-[#4f3b25]">Name</span>
                  <input value={form.name} onChange={(event) => updateField("name", event.target.value)} className="w-full rounded-2xl border border-[#e8dccb] px-4 py-3 text-sm outline-none focus:border-[#ff0099]" />
                </label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="space-y-1">
                    <span className="text-xs font-bold text-[#4f3b25]">Type</span>
                    <select value={form.asset_type} onChange={(event) => updateField("asset_type", event.target.value)} className="w-full rounded-2xl border border-[#e8dccb] px-4 py-3 text-sm outline-none focus:border-[#ff0099]">
                      {TYPE_OPTIONS.filter(Boolean).map((type) => <option key={type} value={type}>{type}</option>)}
                    </select>
                  </label>
                  <label className="space-y-1">
                    <span className="text-xs font-bold text-[#4f3b25]">Status</span>
                    <select value={form.status} onChange={(event) => updateField("status", event.target.value)} className="w-full rounded-2xl border border-[#e8dccb] px-4 py-3 text-sm outline-none focus:border-[#ff0099]">
                      {STATUS_OPTIONS.filter(Boolean).map((status) => <option key={status} value={status}>{status.replace(/_/g, " ")}</option>)}
                    </select>
                  </label>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="space-y-1">
                    <span className="text-xs font-bold text-[#4f3b25]">Group</span>
                    <input value={form.asset_group} onChange={(event) => updateField("asset_group", event.target.value)} className="w-full rounded-2xl border border-[#e8dccb] px-4 py-3 text-sm outline-none focus:border-[#ff0099]" />
                  </label>
                  <label className="space-y-1">
                    <span className="text-xs font-bold text-[#4f3b25]">Role</span>
                    <input value={form.asset_role} onChange={(event) => updateField("asset_role", event.target.value)} className="w-full rounded-2xl border border-[#e8dccb] px-4 py-3 text-sm outline-none focus:border-[#ff0099]" />
                  </label>
                </div>
                <label className="space-y-1">
                  <span className="text-xs font-bold text-[#4f3b25]">File URL</span>
                  <input value={form.file_url} onChange={(event) => updateField("file_url", event.target.value)} className="w-full rounded-2xl border border-[#e8dccb] px-4 py-3 text-sm outline-none focus:border-[#ff0099]" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-bold text-[#4f3b25]">Tags</span>
                  <input value={form.style_tags_text} onChange={(event) => updateField("style_tags_text", event.target.value)} placeholder="style tags" className="w-full rounded-2xl border border-[#e8dccb] px-4 py-3 text-sm outline-none focus:border-[#ff0099]" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-bold text-[#4f3b25]">Generation Prompt</span>
                  {canUseGaneshStagePrompt ? (
                    <button
                      type="button"
                      onClick={useGaneshStagePrompt}
                      className="mb-2 inline-flex items-center justify-center rounded-full border border-[#e2d3bd] bg-[#fffaf2] px-3 py-1.5 text-xs font-bold text-[#6f4a1f] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
                    >
                      Use stage background prompt
                    </button>
                  ) : null}
                  <textarea value={form.generation_prompt} onChange={(event) => updateField("generation_prompt", event.target.value)} rows={4} className="w-full rounded-2xl border border-[#e8dccb] px-4 py-3 text-sm outline-none focus:border-[#ff0099]" />
                  <span className="block text-xs leading-5 text-[#8d7a63]">
                    Save prompt changes first. Regenerate existing overwrites this asset's media; generate variant keeps this asset and creates a new review item.
                    {prefersTransparency ? " Decorative image assets are automatically generated with transparent-background instructions unless marked as background." : ""}
                  </span>
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-bold text-[#4f3b25]">Negative Prompt</span>
                  <textarea value={form.negative_prompt} onChange={(event) => updateField("negative_prompt", event.target.value)} rows={2} className="w-full rounded-2xl border border-[#e8dccb] px-4 py-3 text-sm outline-none focus:border-[#ff0099]" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-bold text-[#4f3b25]">License Notes</span>
                  <textarea value={form.license_notes} onChange={(event) => updateField("license_notes", event.target.value)} rows={2} className="w-full rounded-2xl border border-[#e8dccb] px-4 py-3 text-sm outline-none focus:border-[#ff0099]" />
                </label>
              </div>

              <div className="mt-5 grid gap-3">
                <button type="button" onClick={saveAsset} disabled={isSaving} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#ff0099] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-black disabled:opacity-60">
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />}
                  Save metadata
                </button>
                <button type="button" onClick={generateAsset} disabled={isGenerating || !selectedAsset.generation_prompt} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#251d18] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#ff0099] disabled:cursor-not-allowed disabled:opacity-60">
                  {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles size={16} />}
                  {selectedAsset.file_url?.includes("/pending/") || selectedAsset.status === "failed" ? "Generate media" : "Regenerate existing"}
                </button>
                <button type="button" onClick={generateVariant} disabled={isGenerating || !selectedAsset.generation_prompt} className="inline-flex items-center justify-center gap-2 rounded-full border border-[#e2d3bd] bg-white px-5 py-3 text-sm font-bold text-[#6f4a1f] transition-colors hover:border-[#ff0099] hover:text-[#ff0099] disabled:cursor-not-allowed disabled:opacity-60">
                  {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles size={16} />}
                  Generate as new variant
                </button>
                <button type="button" onClick={() => cleanCheckerboard("safe")} disabled={isCleaning || !prefersTransparency || !selectedAsset.file_url} className="inline-flex items-center justify-center gap-2 rounded-full border border-[#e2d3bd] bg-[#fffaf2] px-5 py-3 text-sm font-bold text-[#6f4a1f] transition-colors hover:border-[#ff0099] hover:text-[#ff0099] disabled:cursor-not-allowed disabled:opacity-60">
                  {isCleaning ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon size={16} />}
                  Clean checkerboard
                </button>
                <button type="button" onClick={() => cleanCheckerboard("aggressive")} disabled={isCleaning || !prefersTransparency || !selectedAsset.file_url} className="inline-flex items-center justify-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-5 py-3 text-sm font-bold text-amber-800 transition-colors hover:border-[#ff0099] hover:text-[#ff0099] disabled:cursor-not-allowed disabled:opacity-60">
                  {isCleaning ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon size={16} />}
                  Aggressive clean
                </button>
                <button type="button" onClick={() => removeSolidBackground("safe")} disabled={isCleaning || !prefersTransparency || !selectedAsset.file_url} className="inline-flex items-center justify-center gap-2 rounded-full border border-[#d7e7d3] bg-[#f5fff3] px-5 py-3 text-sm font-bold text-[#38602e] transition-colors hover:border-[#ff0099] hover:text-[#ff0099] disabled:cursor-not-allowed disabled:opacity-60">
                  {isCleaning ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon size={16} />}
                  Remove solid background
                </button>
                <button type="button" onClick={deleteAsset} disabled={isDeleting} className="inline-flex items-center justify-center gap-2 rounded-full border border-red-200 bg-red-50 px-5 py-3 text-sm font-bold text-red-700 transition-colors hover:bg-red-100 disabled:opacity-60">
                  {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 size={16} />}
                  Delete asset
                </button>
              </div>
            </>
          ) : (
            <div className="rounded-2xl border border-[#e8dccb] bg-[#fbf6ed] p-5 text-sm text-[#766650]">
              Select an asset to review and edit.
            </div>
          )}
        </aside>
      </main>
    </div>
  );
}

export default AdminAssetLibraryPage;
